import Holidays from 'date-holidays';
import moment from 'moment';
import {
  getShouldUseStatesConfigToShowBreakReminder,
  getShouldUseStatesConfigToShowBreakReminderCA,
} from '~/growthbookValue';
import { type IJob, WorkerClassificationTypeEnum } from '~/models';

/**
 * Allow Contractor and Employee if either:
 * - the current stateLocation allows contractors in statesConfigV2, or
 * - the general statesConfigCAV2 allows contractors.
 * Otherwise, only allow Employee.
 */
export const getIsAllowRestBreak = (
  stateLocation: string,
  workerClassification?: WorkerClassificationTypeEnum,
) => {
  const statesConfigV2 = getShouldUseStatesConfigToShowBreakReminder();
  const statesConfigCAV2 = getShouldUseStatesConfigToShowBreakReminderCA();

  return (
    workerClassification !== undefined &&
    (!!statesConfigV2?.[stateLocation]?.contractor ||
      (stateLocation === 'CA' && !!statesConfigCAV2?.contractor) ||
      workerClassification === WorkerClassificationTypeEnum.Employee)
  );
};

/**
 * Determines whether a given date and start time fall within a specified hour limit
 * from the current local time.
 *
 * This function combines a `Date` object (representing the day) and a time string
 * (in 12-hour format, e.g. `"09:30 AM"`) into a single `moment` instance, and then
 * compares it with the current local time.
 *
 * @param localDate - The local date to evaluate (time portion is ignored).
 * @param localStart - The start time in 12-hour format (e.g. `"10:15 AM"`).
 * @param hoursLimit - The maximum number of hours allowed from the current time.
 * @returns `true` if the combined date-time is in the future and within the specified hour limit; otherwise, `false`.
 *
 * @example
 * ```ts
 * const today = new Date('2025-10-21');
 *
 * // Returns true if the current time is before 10:00 AM and within 3 hours
 * isWithinHoursLimit(today, '10:00 AM', 3);
 *
 * // Returns false if the current time is more than 2 hours before 6:00 PM
 * isWithinHoursLimit(today, '6:00 PM', 2);
 * ```
 */
export const isWithinHoursLimit = (localDate: Date, localStart: string, hoursLimit: number) => {
  const time = moment(localStart, 'hh:mm A');

  const localDateTime = moment(localDate).set({
    hour: time.hour(),
    minute: time.minute(),
    second: 0,
    millisecond: 0,
  });

  const currentDateTime = moment();
  const hoursDifference = localDateTime.diff(currentDateTime, 'hours', true);

  return hoursDifference <= hoursLimit;
};

/**
 * Calculates the industrial average percentage based on a new rate compared to the average rate.
 *
 * This function accepts either numeric or string inputs for both `averageRate` and `newRate`.
 * If the values are provided as strings, they are converted to numbers before calculation.
 * It returns how the new rate compares to the average rate as a numeric percentage,
 * which may include decimal values for more precise results.
 *
 * @param averageRate - The reference average rate as a number or string (e.g. `50` or `"50.0"`).
 * @param newRate - The new or proposed rate as a number or string (e.g. `60` or `"60.5"`).
 * @returns A number representing the percentage of `newRate` relative to `averageRate`.
 *          The value may contain decimals (e.g. `112.5`).
 *
 * @example
 * ```ts
 * getIndustrialAverage("50.0", "60.0"); // 120
 * getIndustrialAverage(80, 90.5);       // 113.125
 * getIndustrialAverage(100, 75);        // 75
 * ```
 */
export const getIndustrialAverage = (
  averageRate: string | number,
  newRate: string | number,
): number => {
  const flAvgRate = typeof averageRate === 'string' ? parseFloat(averageRate) : averageRate;
  const flNewRate = typeof newRate === 'string' ? parseFloat(newRate) : newRate;

  return (flNewRate / flAvgRate) * 100;
};

/**
 * Determines whether a given date is a public holiday in the United States.
 *
 * This function uses the `date-holidays` library to check if the specified `Date`
 * corresponds to a recognized holiday in the US.
 *
 * @param date - The date to check.
 * @returns `true` if the date is a recognized US holiday, otherwise `false`.
 *
 * @example
 * ```ts
 * isHolidayDate(new Date('2025-07-04')); // true → Independence Day
 * isHolidayDate(new Date('2025-07-05')); // false
 * ```
 */
export const isHolidayDate = (date: Date): boolean => {
  const instance = new Holidays();
  instance.init('US');

  return !!instance.isHoliday(date);
};

/**
 * Resolves a job's calendar date as `YYYY-MM-DD` in the **office** timezone.
 *
 * `job_datetime` is a UTC instant, so it must be resolved in the job's timezone
 * (not the browser's) — otherwise an afternoon/evening job in a non-UTC market
 * rolls to the wrong calendar day locally. `/jobs/:id` does not always serialise
 * `timezone`, so fall back to the office owner's `time_zone` on the same payload.
 * Returns `undefined` when the fields are missing or the zone is invalid, so
 * callers can treat "no date" as a no-op.
 *
 * @param job - The job to read `job_datetime` + timezone from.
 * @returns `YYYY-MM-DD` in the office timezone, or `undefined`.
 *
 * @example
 * ```ts
 * // job_datetime '2026-07-25T01:00:00Z', zone 'America/New_York' → '2026-07-24'
 * getJobDateInTimezone(job);
 * ```
 */
export const getJobDateInTimezone = (job: IJob): string | undefined => {
  const zone = job.timezone || job.offer_owner?.time_zone;
  if (!job.job_datetime || !zone) return undefined;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(job.job_datetime));
  } catch {
    return undefined;
  }
};
